import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useClickAway, useToggle } from 'ahooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useUserInfo } from 'lib/user';
import { addressFormat } from 'lib/nicknameFormat';
import MintWalletList from './MintWalletList';
import {
  Add03,
  AlertCircleContainedFill,
  ArrowDownContained01,
  CheckContainedFill,
  Wallet,
} from 'lib/lucky-x-icons-2';
import { blockChainIcon } from 'lib/blockChain';
import AddRaffleMintWallet from '../../AddRaffleMintWallet';

interface IMintWalletProps {
  isShow: boolean;
}

const MintWallet: React.FC<IMintWalletProps> = ({ isShow }) => {
  const dispatch = useAppDispatch();
  const mintListRef = useRef<HTMLDivElement>(null);
  const { isLogin, address } = useUserInfo();
  const [showMintList, setShowMintList] = useState(false);
  const { mintWalletListRaffle } = useAppSelector((state) => state.user);

  const {
    raffleInfo: { status, my_entry, project },
    raffleRegisterResult: { validation },
    hasWonMintWallets,
    mintWallet,
  } = useAppSelector((state) => state.raffleDetails);

  useClickAway(() => {
    setShowMintList(false);
  }, mintListRef);

  const closeShowMintList = useCallback(() => {
    setShowMintList(false);
  }, []);

  const isMinted = useMemo(() => {
    return hasWonMintWallets?.findIndex((item) => item?.toLocaleLowerCase() === mintWallet?.toLocaleLowerCase()) !== -1;
  }, [hasWonMintWallets, mintWallet]);

  const getMintWallet = useCallback(() => {
    if (mintWalletListRaffle?.length > 0) {
      return (
        <div className="relative  mt-2 flex items-center justify-between rounded-2xl border-[1px] border-neutral-600 p-4">
          <div className="flex items-center gap-3">
            {isMinted ? (
              <AlertCircleContainedFill size={24} className="text-warn-300" />
            ) : (
              <CheckContainedFill size={24} color="#98FF59" />
            )}

            <div className="w-full text-base  font-normal text-neutral-100 md:w-auto">
              <div className="flex items-center gap-2 font-semibold">
                <img
                  src={blockChainIcon(project?.blockchain || '')}
                  className="h-6 w-6 rounded-full"
                  alt=""
                />
                <div>{addressFormat(mintWallet)}</div>
              </div>
              {isMinted && (
                <div className="text-xs font-normal text-warn-300">
                  Address has already won
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames('cursor-pointer text-white', {
              'rotate-180': showMintList,
            })}
            ref={mintListRef}
            onClick={() => {
              setShowMintList(!showMintList);
            }}
          >
            <ArrowDownContained01 size={24} />
          </div>

          {showMintList && (
            <div className="absolute left-0 right-0 top-[64px] z-10 w-full">
              <MintWalletList closeShowMintList={closeShowMintList} />
            </div>
          )}
        </div>
      );
    }
    return null;
  }, [
    closeShowMintList,
    isMinted,
    mintWallet,
    mintWalletListRaffle?.length,
    project?.blockchain,
    showMintList,
  ]);

  if (
    status === 'ENDED' ||
    status === 'FINALIZED' ||
    !!my_entry ||
    validation ||
    isShow ||
    !isLogin
  ) {
    return null;
  }

  return (
    <div className="text-base font-normal text-neutral-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Wallet size={24} />
          <div>Mint wallet</div>
        </div>
        <AddRaffleMintWallet />
      </div>

      {getMintWallet()}
    </div>
  );
};

export default MintWallet;
